/**
 * Pixel-body rendering.
 *
 * Each body rasterises into a slot of one shared **R16UI atlas of material
 * ids** — never colours. The fragment shader looks the id up in the same
 * palette texture the terrain uses, so a body made of `FUNGAL_MAT` is exactly
 * the green of the fungus growing on the wall behind it, for free and forever.
 *
 * One instanced draw call covers every character on screen. The alternative —
 * a quad per pixel — would be ~700 quads per body and is not affordable at 25
 * enemies.
 *
 * The silhouette rim is computed **in the shader** by testing the eight
 * neighbours inside the slot. That is what satisfies the hard readability
 * requirement (docs/01 §6.4) without a second pass or any CPU work.
 *
 * **The magenta readability rim is gone — the owner's call (2026-08-14).**
 * It outlined every body in the reserved chroma, unlit and untinted, so a
 * character was findable at maximum chaos and in the dark. The owner judged
 * the always-on error-marker colour a bigger cost than the problem, and the
 * problem has shrunk since the rim landed: bodies take the §6.3 light field
 * like the terrain they stand in, and the interior selective outlining below
 * gives every silhouette its own value structure. What is knowingly given up:
 * in a zero-light room the player now vanishes like everything else — which
 * is the reading docs/05 §1's "light is worth more the deeper you are" was
 * always pointing at — and a body against a wall of its own material reads
 * by its lit edges and motion rather than by an outline. If findability
 * regresses in play, the fix is a light-driven weight, not a return to
 * always-on (the discussion is in the session log behind this commit).
 */

import { profiler } from '../core/profiler';
import { M, materialCount } from '../sim/materials';
import { BODY_H, BODY_W, type PixelBody } from '../entity/pixelBody';
import type { Camera } from './camera';
import { GLContext, Uniforms, createProgram } from './gl';
import { createUnlitTexture, type LightTexture } from './light';

export const ATLAS_COLS = 8;
export const ATLAS_ROWS = 8;
export const MAX_BODIES = ATLAS_COLS * ATLAS_ROWS;
const ATLAS_W = ATLAS_COLS * BODY_W;
const ATLAS_H = ATLAS_ROWS * BODY_H;

/** pos(2) half(2) slot(1) flip(1) tint(4) glow(1) = 11 floats */
const STRIDE = 11;

const VERT = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aCorner;
layout(location = 1) in vec2 iPos;
layout(location = 2) in vec2 iHalf;
layout(location = 3) in float iSlot;
layout(location = 4) in float iFlip;
layout(location = 5) in vec4 iTint;
layout(location = 6) in float iGlow;

uniform vec2 uCamera;
uniform vec2 uViewSize;

out vec2 vLocal;
out vec2 vWorld;
flat out float vSlot;
flat out float vFlip;
flat out vec4 vTint;
flat out float vGlow;

void main() {
  vec2 world = iPos + aCorner * iHalf;
  vWorld = world;
  vec2 ndc = (world - uCamera) / (uViewSize * 0.5);
  gl_Position = vec4(ndc.x, -ndc.y, 0.0, 1.0);
  vLocal = aCorner * 0.5 + 0.5;   // [0,1] over the body box
  vSlot = iSlot;
  vFlip = iFlip;
  vTint = iTint;
  vGlow = iGlow;
}`;

const FRAG = `#version 300 es
precision highp float;
precision highp usampler2D;

in vec2 vLocal;
in vec2 vWorld;
flat in float vSlot;
flat in float vFlip;
flat in vec4 vTint;
flat in float vGlow;
out vec4 outColor;

uniform usampler2D uAtlas;
uniform sampler2D uPalette;

// Light field (§6.3).
uniform sampler2D uLight;
uniform vec2 uLightOrigin;
uniform vec2 uLightScale;
uniform float uAmbient;
uniform float uLightGain;

const int BW = ${BODY_W};
const int BH = ${BODY_H};
const int COLS = ${ATLAS_COLS};

ivec2 slotOrigin(float slot) {
  int s = int(slot);
  return ivec2((s % COLS) * BW, (s / COLS) * BH);
}

uint fetchLocal(ivec2 origin, int lx, int ly) {
  if (lx < 0 || ly < 0 || lx >= BW || ly >= BH) return 0u;
  return texelFetch(uAtlas, origin + ivec2(lx, ly), 0).r;
}

bool occupied(ivec2 origin, int lx, int ly) {
  return fetchLocal(origin, lx, ly) != 0u;
}

void main() {
  ivec2 origin = slotOrigin(vSlot);
  float u = vFlip > 0.5 ? 1.0 - vLocal.x : vLocal.x;
  int lx = int(floor(u * float(BW)));
  int ly = int(floor(vLocal.y * float(BH)));

  uint id = fetchLocal(origin, lx, ly);
  if (id == 0u) discard;

  vec4 base = texelFetch(uPalette, ivec2(int(id), 0), 0);
  vec3 col = base.rgb * vTint.rgb;

  // Interior selective outlining: three value clusters per tissue, derived from
  // the silhouette rather than from the palette.
  //
  // A body is coloured by material id out of the shared palette, and that is
  // the mechanism that gives 54 enemies zero authored art — so shade variants
  // are not available to us at any price (docs/18 §6-B). Value structure has to
  // come from geometry instead. A pixel with no neighbour up-and-left is facing
  // the light; one with no neighbour down-and-right is turning away; everything
  // else is the flat mid-tone. That is Derek Yu's selective outlining and
  // Valve's "rim highlights rather than dark outlines", and it is where the
  // dark anchor comes from: measured across the roster, every body in every
  // pose puts at least 5% of itself in each of the three.
  //
  // It *replaces* the old 1.06 - depth*0.22 ramp rather than stacking on
  // it. That ramp was a smooth 8% gradient down a 21-pixel figure, and a
  // gradient underneath edge lighting is exactly the "overly gradated
  // transitions" failure the cluster rule exists to avoid.
  //
  // Six fetches on a body pixel and — unchanged — one on an empty one. At 26
  // bodies of ~150 lit pixels that is ~23k fetches a frame, against a terrain
  // pass that covers the screen.
  bool up = !occupied(origin, lx - 1, ly) || !occupied(origin, lx, ly - 1) ||
            !occupied(origin, lx - 1, ly - 1);
  bool down = !occupied(origin, lx + 1, ly) || !occupied(origin, lx, ly + 1) ||
              !occupied(origin, lx + 1, ly + 1);
  col *= up ? 1.25 : (down ? 0.70 : 1.0);

  // Bodies take the same incident light as the terrain they stand in, so a
  // character lit by a fire at their feet reads as being in the room rather
  // than pasted over it. vGlow is added after, for the same reason terrain's
  // emissive ramp is: it is the body emitting, not receiving.
  vec3 incident = texture(uLight, (vWorld - uLightOrigin) * uLightScale).rgb;
  col *= uAmbient + incident * uLightGain;

  col += vGlow;
  outColor = vec4(col, base.a * vTint.a);
}`;

export class PixelBodyRenderer {
  private glc: GLContext;
  private gl: WebGL2RenderingContext;
  private prog: WebGLProgram;
  private uni: Uniforms;
  private vao: WebGLVertexArrayObject;
  private instanceBuf: WebGLBuffer;
  private atlas: WebGLTexture;
  private palette: WebGLTexture;
  private unlit: WebGLTexture;

  private data = new Float32Array(MAX_BODIES * STRIDE);
  private count = 0;

  /** Staging for one slot upload. */
  private stage = new Uint16Array(BODY_W * BODY_H);

  constructor(glc: GLContext) {
    this.glc = glc;
    const gl = (this.gl = glc.gl);
    this.prog = createProgram(gl, VERT, FRAG, 'pixelBodies');
    this.uni = new Uniforms(gl, this.prog);

    this.atlas = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, this.atlas);
    gl.texStorage2D(gl.TEXTURE_2D, 1, gl.R16UI, ATLAS_W, ATLAS_H);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    this.palette = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, this.palette);
    const n = Math.max(1, materialCount());
    gl.texStorage2D(gl.TEXTURE_2D, 1, gl.RGBA8, n, 1);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, n, 1, gl.RGBA, gl.UNSIGNED_BYTE, M.palette);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);

    this.unlit = createUnlitTexture(gl);

    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);

    const corner = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, corner);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    this.instanceBuf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuf);
    gl.bufferData(gl.ARRAY_BUFFER, this.data.byteLength, gl.DYNAMIC_DRAW);
    const bytes = STRIDE * 4;
    const attr = (loc: number, size: number, off: number) => {
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, size, gl.FLOAT, false, bytes, off * 4);
      gl.vertexAttribDivisor(loc, 1);
    };
    attr(1, 2, 0);
    attr(2, 2, 2);
    attr(3, 1, 4);
    attr(4, 1, 5);
    attr(5, 4, 6);
    attr(6, 1, 10);
    gl.bindVertexArray(null);
  }

  /** Push one slot's pixel data to the GPU. Only when the raster changed. */
  uploadSlot(slot: number, body: PixelBody): void {
    if (slot < 0 || slot >= MAX_BODIES) return;
    const gl = this.gl;
    this.stage.set(body.mat);
    const ox = (slot % ATLAS_COLS) * BODY_W;
    const oy = Math.floor(slot / ATLAS_COLS) * BODY_H;
    gl.bindTexture(gl.TEXTURE_2D, this.atlas);
    gl.texSubImage2D(
      gl.TEXTURE_2D, 0, ox, oy, BODY_W, BODY_H,
      gl.RED_INTEGER, gl.UNSIGNED_SHORT, this.stage,
    );
    body.dirty = false;
    profiler.count('bodyUploads');
  }

  begin(): void {
    this.count = 0;
  }

  push(
    x: number, y: number, halfW: number, halfH: number,
    slot: number, flip: boolean,
    r: number, g: number, bl: number, a: number,
    glow: number,
  ): void {
    if (this.count >= MAX_BODIES) return;
    const o = this.count * STRIDE;
    const d = this.data;
    d[o] = x;
    d[o + 1] = y;
    d[o + 2] = halfW;
    d[o + 3] = halfH;
    d[o + 4] = slot;
    d[o + 5] = flip ? 1 : 0;
    d[o + 6] = r;
    d[o + 7] = g;
    d[o + 8] = bl;
    d[o + 9] = a;
    d[o + 10] = glow;
    this.count++;
  }

  /** `light` is optional; omitted, bodies render exactly as they did before §6.3. */
  flush(camera: Camera, light?: LightTexture): void {
    if (this.count === 0) return;
    const gl = this.gl;
    profiler.begin('render');

    gl.useProgram(this.prog);
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuf);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.data.subarray(0, this.count * STRIDE));

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.atlas);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.palette);
    if (light) {
      light.bind(2);
      this.uni.f2('uLightOrigin', light.originX, light.originY);
      this.uni.f2('uLightScale', light.scaleX, light.scaleY);
      this.uni.f1('uAmbient', light.ambient);
      this.uni.f1('uLightGain', light.gain);
    } else {
      gl.activeTexture(gl.TEXTURE2);
      gl.bindTexture(gl.TEXTURE_2D, this.unlit);
      this.uni.f2('uLightOrigin', 0, 0);
      this.uni.f2('uLightScale', 0, 0);
      this.uni.f1('uAmbient', 1);
      this.uni.f1('uLightGain', 0);
    }
    this.uni.i1('uAtlas', 0);
    this.uni.i1('uPalette', 1);
    this.uni.i1('uLight', 2);
    this.uni.f2('uCamera', camera.renderX, camera.renderY);
    this.uni.f2('uViewSize', camera.viewW, camera.viewH);

    gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, this.count);
    profiler.count('drawCalls');
    profiler.setCount('bodies', this.count);

    gl.bindVertexArray(null);
    void this.glc;
    profiler.end('render');
  }

  refreshPalette(): void {
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, this.palette);
    gl.texSubImage2D(
      gl.TEXTURE_2D, 0, 0, 0, materialCount(), 1,
      gl.RGBA, gl.UNSIGNED_BYTE, M.palette,
    );
  }
}
